import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useAdminMe } from '../auth/AdminContext';
import { reset, track } from '../lib/amplitude';
import { config } from '../lib/env';
import { Wordmark } from './Clapper';
import { AppLink, usePathname } from './Nav';
import { Badge, cx } from './ui';

/**
 * The signed-in chrome. A stage badge sits beside the wordmark because the
 * same build serves development and production data, and an admin looking at
 * the wrong one should be able to tell at a glance.
 */

const MENU_ITEM =
  'flex w-full items-center gap-2 rounded px-2.5 py-2 text-left text-body text-chalk-dim ' +
  'transition-colors duration-120 hover:bg-slate-800 hover:text-chalk';

const NAV: Array<{ to: string; label: string }> = [
  { to: '/', label: 'Overview' },
  { to: '/users', label: 'Users' },
  { to: '/teams', label: 'Teams' },
  { to: '/recordings', label: 'Recordings' },
  { to: '/requests', label: 'Requests' },
  { to: '/admins', label: 'Admins' },
];

function useDismissable(open: boolean, close: () => void) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [close, open]);
  return containerRef;
}

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  const pathname = usePathname();
  const active = to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <AppLink
      to={to}
      className={cx(
        'rounded px-2.5 py-1.5 text-body transition-colors duration-120',
        active ? 'bg-slate-800 text-chalk' : 'text-chalk-dim hover:bg-slate-850 hover:text-chalk',
      )}
    >
      {children}
    </AppLink>
  );
}

function StageBadge() {
  if (config.stage === 'production') return <Badge tone="record">Production</Badge>;
  if (config.stage === 'development') return <Badge tone="caution">Development</Badge>;
  return <Badge tone="neutral">Local</Badge>;
}

function AccountMenu() {
  const { signOut } = useAuth();
  const me = useAdminMe();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useDismissable(open, () => setOpen(false));
  const name = me.name?.trim() || me.email.split('@')[0];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-hairline bg-slate-800 text-caption font-medium uppercase text-chalk-dim transition-colors duration-120 hover:border-slate-600 hover:text-chalk"
      >
        {name.slice(0, 1)}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-56 animate-fade-up rounded-md border border-hairline bg-slate-850 p-1 shadow-raised"
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-body text-chalk">{name}</p>
            <p className="truncate text-caption text-chalk-faint">{me.email}</p>
          </div>
          <div className="my-1 h-px bg-hairline" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              track('Signed Out');
              // Nothing cached here belongs to the next person to sign in, and
              // neither does the analytics identity.
              void signOut().then(() => {
                queryClient.clear();
                reset();
              });
            }}
            className={MENU_ITEM}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="sticky top-0 z-20 border-b border-hairline bg-slate-950/95 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
          <AppLink to="/" className="mr-1 flex items-center gap-2.5" aria-label="Overview">
            <Wordmark />
            <span className="text-caption uppercase tracking-[0.12em] text-chalk-faint">Admin</span>
          </AppLink>
          <StageBadge />

          <nav className="ml-2 hidden items-center gap-0.5 md:flex">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <AccountMenu />
          </div>
        </div>
        <nav className="flex gap-0.5 overflow-x-auto px-3 pb-2 md:hidden">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to}>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">{children}</main>

      <footer className="border-t border-hairline">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-5 gap-y-2 px-4 py-5 text-caption text-chalk-faint sm:px-6">
          <span>Slate Admin</span>
          <span className="truncate">{config.apiUrl}</span>
        </div>
      </footer>
    </div>
  );
}
